import { CheckIcon, CloseIcon } from 'components/svgs';

export type StepperStepState =
  | 'done'
  | 'current'
  | 'upcoming'
  | 'warning'
  | 'failed';

export type StepperStep = {
  /** Stable id of the step, used as the React key. */
  key: string;
  label: string;
  /** One short line under the label, e.g. a progress count. */
  sub?: string;
  state: StepperStepState;
};

export type StepperProps = {
  steps: StepperStep[];
  className?: string;
};

const circleClasses: Record<StepperStepState, string> = {
  done: 'bg-design-green text-white',
  current: 'border-2 border-yellow',
  upcoming: 'border-2 border-dots-grey',
  warning: 'bg-yellow text-design-black',
  failed: 'bg-darker-red text-white',
};

const labelClasses: Record<StepperStepState, string> = {
  done: '',
  current: 'font-semibold',
  upcoming: 'text-light-black dark:text-sublte-white',
  warning: 'font-semibold',
  failed: 'font-semibold text-darker-red',
};

/** Read out after the label, so the state is not colour-only. */
const stateNames: Record<StepperStepState, string> = {
  done: 'done',
  current: 'in progress',
  upcoming: 'not started',
  warning: 'completed with failures',
  failed: 'failed',
};

const Stepper = ({ steps, className }: StepperProps) => (
  <ol className={`flex items-start ${className}`}>
    {steps.map((step, index) => (
      <li
        key={step.key}
        className="flex flex-grow items-start last:flex-grow-0"
        aria-current={step.state === 'current' ? 'step' : undefined}
      >
        <div className="flex w-28 flex-col items-center text-center">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full ${
              circleClasses[step.state]
            }`}
          >
            {step.state === 'done' && <CheckIcon />}
            {step.state === 'warning' && (
              <span className="text-xs font-bold">!</span>
            )}
            {step.state === 'failed' && <CloseIcon />}
            {step.state === 'current' && (
              <span className="h-2 w-2 rounded-full bg-yellow" />
            )}
          </span>
          <span className={`mt-2 text-sm ${labelClasses[step.state]}`}>
            {step.label}
          </span>
          {step.sub && (
            <span className="text-xs text-light-black dark:text-sublte-white">
              {step.sub}
            </span>
          )}
          <span className="sr-only">{stateNames[step.state]}</span>
        </div>
        {index < steps.length - 1 && (
          <span
            aria-hidden="true"
            className={`mt-3 h-0.5 flex-grow ${
              step.state === 'done'
                ? 'bg-design-green'
                : 'bg-design-secondary-black'
            }`}
          />
        )}
      </li>
    ))}
  </ol>
);

Stepper.defaultProps = {
  className: '',
};

export default Stepper;
